import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  Button, 
  Card, 
  CardMedia, 
  CardContent, 
  Fab, 
  Tooltip,
  Zoom,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  LocalGasStation as GasIcon,
  Fire as FireIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import homeBn from '../i18n/bn/home';
import homeEn from '../i18n/en/home';
import homeNp from '../i18n/np/home';
import homePk from '../i18n/pk/home';
import homeDe from '../i18n/de/home';
import homeBt from '../i18n/bt/home';
import homeSl from '../i18n/sl/home';
import homeAf from '../i18n/af/home';
import homeCn from '../i18n/cn/home';
import Footer from '../components/Footer';
import HeroSection from '../components/HeroSection';
import '../styles/HeroSection.css'; // Import original HeroSection styles
// Import Swiper React components
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

// Styled components
const BazarCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease',
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[10],
  },
}));

const BazarImage = styled('img')({
  width: '100%',
  height: 200,
  objectFit: 'cover',
  borderRadius: 8,
  marginBottom: 16,
});

const CategoryCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  height: 200,
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  '&:hover': {
    '& .MuiCardMedia-root': {
      transform: 'scale(1.1)',
    },
  },
}));

const CategoryMedia = styled(CardMedia)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.3s ease-in-out',
}));

const CategoryOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
}));

const SectionTitle = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(6),
}));

const ActionButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(1),
  borderRadius: 20,
}));

// Styled components for partner slider
const PartnerSliderContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: '20px',
  left: 0,
  right: 0,
  background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 30%, rgba(0,0,0,0.6) 100%)',
  backdropFilter: 'blur(15px)',
  WebkitBackdropFilter: 'blur(15px)',
  padding: theme.spacing(3, 2, 3, 2),
  zIndex: 10,
  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '16px 16px 0 0',
  margin: '0 10px',
  maxHeight: '200px',
  overflow: 'hidden',
}));

const PartnerLogoWrapper = styled(Box)(({ theme }) => ({
  width: 120,
  height: 50,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const PartnerLogo = styled('img')({
  maxHeight: '100%',
  maxWidth: '100%',
  height: 'auto',
  width: 'auto',
  objectFit: 'contain',
  transition: 'transform 0.3s ease, opacity 0.3s ease',
  opacity: 0.9,
  display: 'block',
  '&:hover': {
    opacity: 1,
    transform: 'scale(1.05)',
  },
});

const PartnerSlide = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: 70,
  padding: theme.spacing(1),
  borderRadius: theme.spacing(1),
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  },
}));

// Search Section Styled Components
const SearchSection = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '40%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  zIndex: 5,
  padding: theme.spacing(0, 2),
}));

const SearchContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
}));

const SearchField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(10px)',
    borderRadius: theme.spacing(3),
    fontSize: '1.1rem',
    height: '60px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    border: '2px solid rgba(255, 255, 255, 0.2)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
      border: '2px solid rgba(94, 53, 177, 0.3)',
    },
    '&.Mui-focused': {
      backgroundColor: 'white',
      boxShadow: '0 12px 40px rgba(94, 53, 177, 0.2)',
      border: '2px solid #5e35b1',
    },
    '& fieldset': {
      border: 'none',
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '1.1rem',
    padding: theme.spacing(2),
    '&::placeholder': {
      color: 'rgba(0, 0, 0, 0.6)',
      fontWeight: 500,
    },
  },
}));

const SearchButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: '#5e35b1',
  color: 'white',
  borderRadius: '50%',
  width: 48,
  height: 48,
  marginLeft: theme.spacing(1),
  boxShadow: '0 4px 16px rgba(94, 53, 177, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#4527a0',
    transform: 'scale(1.1)',
    boxShadow: '0 6px 20px rgba(94, 53, 177, 0.4)',
  },
}));

const Home = () => {
  // Real partner logos from public/images/partners/
  const partners = [
    // { id: 1, name: 'UNICEF', logo: '/images/partners/unicef.svg' },
    { id: 2, name: 'Grameen Bank', logo: '/images/partners/grameen_bank.svg' },
    { id: 3, name: 'Pathao', logo: '/images/partners/pathao.svg' },
    { id: 4, name: 'Grameen Delivery', logo: '/images/partners/grameendelivery.png' },
    { id: 5, name: 'Dutch Bangla Bank', logo: '/images/partners/dutch_bangla_bank.svg' },
    { id: 6, name: 'Islami Bank', logo: '/images/partners/islami-bank.svg' },
    { id: 7, name: 'bKash', logo: '/images/partners/bkash.svg' },
    { id: 8, name: 'Nagad', logo: '/images/partners/nagad.svg' },
    { id: 9, name: 'Mokam', logo: '/images/partners/mokam.svg' },
    { id: 10, name: 'Steadfast', logo: '/images/partners/steadfast.svg' },
    { id: 10, name: 'Amber IT', logo: '/images/partners/amberit.png' },
    { id: 10, name: 'sslcommerz', logo: '/images/partners/sslcommerz.png' },
  ];

  // Typewriter effect state and logic
  const lang = typeof window !== 'undefined' ? (localStorage.getItem('preferredLanguage') || 'bn') : 'bn';
  const thome = lang === 'en' ? homeEn : 
                lang === 'np' ? homeNp : 
                lang === 'pk' ? homePk : 
                lang === 'bt' ? homeBt : 
                lang === 'de' ? homeDe : 
                lang === 'sl' ? homeSl : 
                lang === 'af' ? homeAf : 
                lang === 'cn' ? homeCn : 
                homeBn;
  const placeholders = thome.search.typewriterPlaceholders;
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [currentPlaceholder, setCurrentPlaceholder] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [charIndex, setCharIndex] = useState(0);

  useEffect(() => {
    const typeSpeed = 150;
    const eraseSpeed = 75;
    const pauseTime = 1500;

    let timer;
    
    if (isTyping) {
      if (charIndex < placeholders[placeholderIndex].length) {
        timer = setTimeout(() => {
          setCurrentPlaceholder(prevText => 
            prevText + placeholders[placeholderIndex][charIndex]
          );
          setCharIndex(charIndex + 1);
        }, typeSpeed);
      } else {
        timer = setTimeout(() => {
          setIsTyping(false);
        }, pauseTime);
      }
    } else {
      if (charIndex > 0) {
        timer = setTimeout(() => {
          setCurrentPlaceholder(prevText => prevText.slice(0, -1));
          setCharIndex(charIndex - 1);
        }, eraseSpeed);
      } else {
        setIsTyping(true);
        setPlaceholderIndex((placeholderIndex + 1) % placeholders.length);
      }
    }

    return () => clearTimeout(timer);
  }, [charIndex, isTyping, placeholderIndex, placeholders]);

  return (
    <>
      <Box sx={{ 
        height: '100vh', 
        overflow: 'hidden',
        position: 'relative'
      }}>
        <Header />
        <Box sx={{ 
          position: 'relative',
          height: 'calc(100vh - 64px)', // Subtract header height
          overflow: 'hidden'
        }}>
          {/* Background Video Section - Using original HeroSection styling */}
          <div className="hero-section" style={{ position: 'absolute', width: '100%', height: '100%' }}>
            <video 
              className="hero-video-background"
              autoPlay 
              muted 
              loop
            >
              <source src="/videos/default.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <div className="video-overlay"></div>
          </div>

          {/* Search Section - Positioned in the middle */}
          <SearchSection>
            <SearchContainer maxWidth="md">
              <Typography 
                variant="h3" 
                component="h1" 
                sx={{ 
                  color: 'white',
                  fontWeight: 700,
                  mb: 2,
                  textAlign: 'center',
                  textShadow: '2px 2px 8px rgba(0,0,0,0.7)',
                  fontSize: { xs: '2rem', md: '3rem' }
                }}
              >
                {thome.hero.title}
              </Typography>
              <Typography 
                variant="h5" 
                component="h2" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontWeight: 400,
                  mb: 4,
                  textAlign: 'center',
                  textShadow: '1px 1px 4px rgba(0,0,0,0.7)',
                  fontSize: { xs: '1.2rem', md: '1.5rem' }
                }}
              >
                {thome.hero.subtitle}
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                width: '100%', 
                maxWidth: 600 
              }}>
                <SearchField
                  fullWidth
                  placeholder={currentPlaceholder || thome.search.placeholderDefault}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchButton edge="end">
                          <SearchIcon />
                        </SearchButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </SearchContainer>
          </SearchSection>
          
          {/* Partner Logo Slider */}
          <PartnerSliderContainer>
            <Container maxWidth="xl" sx={{ px: 2 }}>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography 
                  variant="h5" 
                  component="h3" 
                  sx={{ 
                    color: 'white',
                    fontWeight: 700,
                    textShadow: '2px 2px 8px rgba(0,0,0,0.7)',
                    letterSpacing: '0.5px',
                    mb: 0.5
                  }}
                >
                  {thome.partners.heading}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontWeight: 400,
                    textShadow: '1px 1px 4px rgba(0,0,0,0.5)',
                  }}
                >
                  {thome.partners.subheading}
                </Typography>
              </Box>
              <Box sx={{ 
                overflow: 'hidden',
                mx: 2 // Add horizontal margin to prevent edge overflow
              }}>
                <Swiper
                  modules={[Autoplay]}
                  spaceBetween={15}
                  slidesPerView={2}
                  centeredSlides={false}
                  autoplay={{ 
                    delay: 3000, 
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true
                  }}
                  loop={true}
                  breakpoints={{
                    480: { slidesPerView: 2, spaceBetween: 15 },
                    640: { slidesPerView: 3, spaceBetween: 20 },
                    768: { slidesPerView: 4, spaceBetween: 20 },
                    1024: { slidesPerView: 5, spaceBetween: 25 },
                    1200: { slidesPerView: 6, spaceBetween: 25 },
                  }}
                  style={{
                    overflow: 'visible',
                    height: '80px' // Fixed height to prevent overflow
                  }}
                >
                  {partners.map((partner) => (
                    <SwiperSlide key={partner.id} style={{ display: 'flex', justifyContent: 'center' }}>
                      <PartnerSlide>
                        <PartnerLogoWrapper>
                          <PartnerLogo 
                            src={partner.logo} 
                            alt={partner.name}
                            title={partner.name}
                          />
                        </PartnerLogoWrapper>
                      </PartnerSlide>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </Box>
            </Container>
          </PartnerSliderContainer>
        </Box>
      </Box>
    </>
  );
};

export default Home;